import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, Stack, useMediaQuery } from '@mui/material';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
    useGetAllGuestsQuery,
    useGetBillingReportQuery,
    useGetCompanyGuestsInfoQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import { formatCurrencyShort } from '../../../../shared/utils';
import FormMultiSelect from '../../../shared/FormMultiSelect';
import FormSelect from '../../../shared/FormSelect';
import { type MRT_ColumnDef } from 'mantine-react-table';
import GenericTable, {
    LinkType,
    tableBodyCellsStyles,
    tableHeadCellStyles,
} from '../../../shared/GenericTable';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';

const BillingReport = () => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const loggedInUser = UserRole();
    const { data: guestCompanyInfo } = useGetCompanyGuestsInfoQuery(
        `/guests/${loggedInUser?.company}`,
    );

    const schema = z.object({
        year: z.number().nullable().optional(),
        guestIds: z.array(z.number()).optional(),
    });

    type BillingReportFilters = z.infer<typeof schema>;
    const {
        reset,
        control,
        watch,
        formState: { isDirty, isLoading },
    } = useForm<BillingReportFilters>({
        resolver: zodResolver(schema),
        defaultValues: {
            year: null,
            guestIds: [],
        },
    });

    const { data: billingReportData } = useGetBillingReportQuery({
        ...(watch('year') ? { year: watch('year') } : {}),
        ...(watch('guestIds') ? { guestIds: watch('guestIds') } : {}),
    });

    const modifiedBillingReportData = billingReportData?.map((item) => ({
        ...item,
        daysFromDueDate: Math.ceil(
            (new Date().getTime() - new Date(item.dueDate).getTime()) /
                (1000 * 60 * 60 * 24),
        ),
    }));

    const processedData = useMemo(() => {
        if (!modifiedBillingReportData) return [];
        return modifiedBillingReportData.map((row: any) => ({
            ...row,
        }));
    }, [modifiedBillingReportData]);

    const { data: allGuests } = useGetAllGuestsQuery(`/guests`, {
        skip: loggedInUser?.role !== 'admin',
    });

    const now = new Date();
    const currentYear = now.getFullYear();

    const years: { value: number; label: string }[] = [];
    const startYear =
        loggedInUser?.role === 'admin'
            ? 2023
            : new Date(guestCompanyInfo?.createdAt).getFullYear();
    for (let year = currentYear; year >= startYear; year--) {
        years.push({
            value: year,
            label: `${year}`,
        });
    }

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'guestName',
            header: 'Guest Name',
            size: 130,
        },
        {
            accessorKey: 'period',
            header: 'Period',
            size: 170,
            accessorFn: (row) => {
                const from = row.from
                    ? moment(row.from).format('D MMM')
                    : 'N/A';
                const to = row.to
                    ? moment(row.to).format('D MMM, YYYY')
                    : 'N/A';
                return `${from} - ${to}`;
            },
        },
        {
            accessorKey: 'id',
            header: 'Ref.',
            size: 80,
            accessorFn: (row) => `#${row.id}`,
        },
        {
            accessorKey: 'billedTo',
            header: 'Billed To',
            Cell: ({ row }) => {
                const { firstName, lastName, streetAddress } = row.original;
                const name = `${firstName || ''} ${lastName || ''}`.trim();
                return (
                    <div>
                        <div>{name || 'N/A'}</div>
                        {streetAddress && <div>{streetAddress}</div>}
                    </div>
                );
            },
        },
        {
            accessorKey: 'dueDate',
            header: 'Due Date',
            size: 130,
            accessorFn: (row) =>
                row.dueDate ? moment(row.dueDate).format('D MMM YYYY') : 'N/A',
        },
        {
            accessorKey: 'total',
            header: 'Amount (inc GST)',
            size: 120,
            filterFn: 'betweenInclusive',
            accessorFn: (row) => row.total ?? 0,
            Cell: ({ cell }) =>
                formatCurrencyShort(cell.getValue<number>() ?? 0),
            mantineTableHeadCellProps: {
                align: 'right',
            },
            mantineTableBodyCellProps: {
                align: 'right',
            },
        },
        {
            accessorKey: 'lessThan7Days',
            header: '<7 days',
            size: 140,
            filterFn: 'betweenInclusive',
            accessorFn: (row) =>
                row.amountDue === 0 ||
                !(row.daysFromDueDate < 7 && row.daysFromDueDate > 0)
                    ? -1
                    : row.total,
            Cell: ({ cell }) => {
                const val = cell.getValue<number>();
                return val <= -1 ? '' : formatCurrencyShort(val);
            },
            mantineTableHeadCellProps: { align: 'right' },
            mantineTableBodyCellProps: { align: 'right' },
        },
        {
            accessorKey: 'lessThan14Days',
            header: '<14 days',
            size: 140,
            filterFn: 'betweenInclusive',
            accessorFn: (row) =>
                row.amountDue === 0 ||
                !(row.daysFromDueDate < 14 && row.daysFromDueDate >= 7)
                    ? -1
                    : row.total,
            Cell: ({ cell }) => {
                const val = cell.getValue<number>();
                return val <= -1 ? '' : formatCurrencyShort(val);
            },
            mantineTableHeadCellProps: { align: 'right' },
            mantineTableBodyCellProps: { align: 'right' },
        },
        {
            accessorKey: 'lessThan30Days',
            header: '<30 days',
            size: 140,
            filterFn: 'betweenInclusive',
            accessorFn: (row) =>
                row.amountDue === 0 ||
                !(row.daysFromDueDate < 30 && row.daysFromDueDate >= 14)
                    ? -1
                    : row.total,
            Cell: ({ cell }) => {
                const val = cell.getValue<number>();
                return val <= -1 ? '' : formatCurrencyShort(val);
            },
            mantineTableHeadCellProps: { align: 'right' },
            mantineTableBodyCellProps: { align: 'right' },
        },
        {
            accessorKey: 'moreThan30Days',
            header: '30+ days',
            size: 140,
            filterFn: 'betweenInclusive',
            accessorFn: (row) =>
                row.amountDue === 0 || row.daysFromDueDate < 30
                    ? -1
                    : row.total,
            Cell: ({ cell }) => {
                const val = cell.getValue<number>();
                return val <= -1 ? '' : formatCurrencyShort(val);
            },
            mantineTableHeadCellProps: { align: 'right' },
            mantineTableBodyCellProps: { align: 'right' },
        },
        {
            accessorKey: 'fullyPaidAt',
            header: 'Paid Date',
            size: 130,
            accessorFn: (row) =>
                row.fullyPaidAt
                    ? moment(row.fullyPaidAt).format('D MMM YYYY')
                    : 'N/A',

            mantineTableHeadCellProps: {
                style: { ...tableHeadCellStyles, paddingLeft: 40 },
            },
            mantineTableBodyCellProps: {
                style: { ...tableBodyCellsStyles, paddingLeft: 40 },
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            accessorFn: (row) => (row.amountDue === 0 ? 'Paid' : 'Unpaid'),
            size: 150,
            filterVariant: 'select',
            filterFn: (row, columnId, filterValue) => {
                if (!filterValue) return true;
                const value = row.getValue(columnId);
                return value === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Paid', value: 'Paid' },
                    { label: 'Unpaid', value: 'Unpaid' },
                ],
                clearable: true,
            },
        },
    ];

    const linkState = {
        name: 'viewReservation',
        component: 'reports',
        url: 'reports/reservationsReport',
    };
    return (
        <Stack spacing={3} paddingTop={3}>
            <Grid
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                }}
            >
                <Grid container item columnSpacing={2} rowSpacing={2}>
                    {' '}
                    <Grid item xs={12} md={4} lg={3}>
                        <FormSelect
                            name='year'
                            control={control}
                            options={years}
                            label='Select Year'
                        />
                    </Grid>
                    {loggedInUser?.role === 'admin' && (
                        <Grid item xs={12} md={4} lg={3}>
                            <FormMultiAutocomplete
                                name='guestIds'
                                control={control}
                                options={
                                    allGuests
                                        ? allGuests?.map((guest: any) => ({
                                              value: guest.id,
                                              label: guest.name,
                                          }))
                                        : []
                                }
                                label='Select Guests'
                            />
                        </Grid>
                    )}
                    <Grid
                        item
                        xs={12}
                        md={loggedInUser?.role === 'admin' ? 4 : 8}
                        lg={loggedInUser?.role === 'admin' ? 6 : 9}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        {' '}
                        <Grid>
                            {' '}
                            <CustomButton
                                type='button'
                                variant='contained'
                                color='neutral'
                                // fullWidth
                                onClick={() => {
                                    reset();
                                }}
                                disabled={!isDirty}
                                height='40px'
                            >
                                Clear Filters
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Box
                sx={{
                    height: 800,
                    width: 'auto',
                }}
            >
                {' '}
                {!billingReportData ? (
                    <Grid
                        sx={{
                            height: isIpad ? '50vh' : isMobile ? '5px' : '50vh',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            alignContent: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <LoadingComponent />
                    </Grid>
                ) : (
                    <GenericTable
                        columns={columns}
                        data={processedData}
                        tableName='7DAY-Guest-Invoices'
                        loading={!modifiedBillingReportData}
                        linkType={LinkType.Invoice}
                        linkState={linkState}
                    />
                )}
            </Box>
        </Stack>
    );
};

export default BillingReport;
